import '@/playlist/games/letterParty/styles/letter-party-end-screen.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import { showFireworksConfetti } from '@/pages/learningPath/Helper';

import AudioToggleButton from '@/components/audioToggle/AudioToggleButton';
import BackButton from '@/components/button/BackButton';
import Button from '@/components/button/Button';
import Tooltip, { TooltipColors, TooltipSizes } from '@/components/tooltip/Tooltip';
import GameEndScreenMilepost from '@/playlist/games/common/components/GameEndScreenMilepost';

import djLevel1 from '@/playlist/games/letterParty/assets/lottie/dj-level-1.json';
import djLevel4 from '@/playlist/games/letterParty/assets/lottie/dj-level-4.json';
import gull from '@/playlist/games/letterParty/assets/lottie/gull.json';
import palmTreeLevel1 from '@/playlist/games/letterParty/assets/lottie/palm-tree-level-1.json';
import palmTreeLevel4 from '@/playlist/games/letterParty/assets/lottie/palm-tree-level-4.json';

const TOOLTIP_ANCHOR_ID = 'letter-party-endscreen-tooltip-anchor';

interface EndScreenProps {
	currentScore: number;
	bestScore: number;
	progressStarsEarned?: number;
	totalStars?: number;
	earnedStars?: number;
	previousEarned?: number;
	levelImage?: string;
	levelName?: string;
	levelTitle?: string;
	levelCompleted?: any;
	onClose: () => void;
	onReplay?: () => void;
}

const EndScreen = ({
	currentScore,
	bestScore,
	progressStarsEarned,
	totalStars,
	earnedStars,
	previousEarned,
	levelImage,
	levelName,
	levelTitle,
	levelCompleted,
	onClose,
	onReplay,
}: EndScreenProps) => {
	const { t } = useTranslation();
	const [showProgressAnimation, setShowProgressAnimation] = useState(false);
	const tooltipAnchorRef = useRef<HTMLDivElement>(null);
	const scoreContentRef = useRef<HTMLDivElement>(null);
	const milepostContainerRef = useRef<HTMLDivElement>(null);

	const isNewHighScore = currentScore > bestScore;
	const displayScore = isNewHighScore ? currentScore : bestScore;

	const djLevel = isNewHighScore ? djLevel4 : djLevel1;
	const palmTree = isNewHighScore ? palmTreeLevel4 : palmTreeLevel1;

	const djAnimationData = useMemo(() => {
		const clonedData = structuredClone(djLevel);
		// Find and replace the text value "77" with currentScore
		const replaceText = (obj: any) => {
			if (obj && typeof obj === 'object') {
				if (obj.t === '77') {
					obj.t = String(currentScore);
				}
				Object.values(obj).forEach(replaceText);
			}
		};
		replaceText(clonedData);
		return clonedData;
	}, [currentScore, djLevel]);

	useEffect(() => {
		if (isNewHighScore) {
			showFireworksConfetti('endscreen-confetti', 3000);
		}
	}, [isNewHighScore]);

	const handleNext = () => {
		if (totalStars !== undefined && earnedStars !== undefined) {
			const tl = gsap.timeline();

			tl.to(tooltipAnchorRef.current, {
				opacity: 0,
				y: 50,
				duration: 0.4,
				ease: Power1.easeInOut,
			});

			tl.to(scoreContentRef.current, {
				opacity: 0,
				y: 50,
				zIndex: -1,
				duration: 0.4,
				ease: Power1.easeInOut,
			});

			tl.to(milepostContainerRef.current, {
				autoAlpha: 1,
				duration: 0.5,
				ease: Power1.easeInOut,
				onComplete: () => {
					setShowProgressAnimation(true);
				},
			});
		} else {
			onClose();
		}
	};

	const handleReplay = () => {
		if (onReplay) {
			onReplay();
		} else {
			onClose();
		}
	};

	const tooltipText = isNewHighScore
		? t('letter_party.endscreen.tooltip.new_high_score')
		: t('letter_party.endscreen.tooltip.no_high_score');

	const centerText = isNewHighScore
		? t('letter_party.endscreen.title.new_high_score')
		: t('letter_party.endscreen.title.no_high_score');

	return (
		<div className="letter-party-game__endscreen">
			<div className="letter-party-game__endscreen-background">
				<div className="letter-party-game__endscreen-background-backward" />
				<Lottie
					animationData={palmTree}
					loop
					autoplay
					className="letter-party-game__endscreen-palm-tree-right"
				/>
				<Lottie
					animationData={djAnimationData}
					loop
					autoplay
					className="letter-party-game__endscreen-dj"
				/>
				<Lottie
					animationData={gull}
					loop
					autoplay
					className="letter-party-game__endscreen-gull"
				/>
				<div className="letter-party-game__endscreen-background-forward" />
			</div>

			<div className="letter-party-game__endscreen-controls">
				<div className="container">
					<BackButton onClick={onClose} />
					<AudioToggleButton tooltip={t('letter_party.audio_toggle.tooltip')} />
				</div>
			</div>

			<div
				id={TOOLTIP_ANCHOR_ID}
				className="letter-party-game__endscreen-tooltip-anchor"
				ref={tooltipAnchorRef}
			/>
			<Tooltip
				place="top"
				title={tooltipText}
				anchor={TOOLTIP_ANCHOR_ID}
				color={TooltipColors.LIGHT}
				size={TooltipSizes.LARGE}
				isOpen
				className="letter-party-game__endscreen-tooltip"
			/>

			{totalStars !== undefined && earnedStars !== undefined && (
				<GameEndScreenMilepost
					ref={milepostContainerRef}
					progressStarsEarned={progressStarsEarned}
					totalStars={totalStars}
					earnedStars={earnedStars}
					previousEarned={previousEarned}
					levelImage={levelImage}
					levelName={levelName}
					levelTitle={levelTitle}
					levelCompleted={levelCompleted}
					showProgressAnimation={showProgressAnimation}
					onReplay={handleReplay}
					onClose={onClose}
					replayButtonLabel={t('letter_party.endscreen.replay')}
					closeButtonLabel={t('letter_party.endscreen.close')}
				/>
			)}

			<div className="letter-party-game__endscreen-content" ref={scoreContentRef}>
				<div className="letter-party-game__endscreen-title">
					<span
						className="letter-party-game__endscreen-text"
						dangerouslySetInnerHTML={{
							__html: centerText,
						}}
					/>
					{!isNewHighScore && (
						<span className="letter-party-game__endscreen-score">{displayScore}</span>
					)}
				</div>
				<Button
					type="primary"
					onClick={handleNext}
					iconName="icon-arrow-circle-right"
					title={t('letter_party.endscreen.next')}
				/>
			</div>

			<canvas id="endscreen-confetti" className="letter-party-game__endscreen-confetti" />
		</div>
	);
};

export default EndScreen;
